import { useState, useEffect } from 'preact/hooks';
import { route } from 'preact-router';
import { MainLayout } from '../../components/MainLayout';
import { useScopeContext } from '../../contexts/ScopeContext';
import { getAgentCardColor, pageGradients, decorativeBlobs, headingGradient } from '../../config/theme';
import { getAuthHeaders } from '../../utils/auth';

interface Team {
  teamId: string;
  name: string;
  description?: string;
  memberCount: number;
  role: 'admin' | 'member';
  status: 'active' | 'deactivated';
}

interface Agent {
  id: string;
  name: string;
  description?: string;
  role?: string;
  status: string;
  lastUsed?: string;
  createdBy?: string;
  messageCount?: number;
}

interface TeamDetailsPageProps {
  teamId: string;
}

export function TeamDetailsPage({ teamId }: TeamDetailsPageProps) {
  const scope = useScopeContext();
  const [team, setTeam] = useState<Team | null>(null);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [userRole, setUserRole] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadTeamDetails();
    loadAgents();
  }, [teamId]);

  const loadTeamDetails = async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/teams/${teamId}`, {
        headers: {
          ...(await getAuthHeaders())
        }
      });

      if (!response.ok) {
        throw new Error('Failed to load team details');
      }

      const data = await response.json();
      setTeam(data.team);
      setUserRole(data.userRole);
    } catch (err) {
      console.error('Error loading team details:', err);
      setError(err instanceof Error ? err.message : 'Failed to load team');
    }
  };

  const loadAgents = async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/teams/${teamId}/agents`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(await getAuthHeaders())
        }
      });

      if (!response.ok) {
        throw new Error('Failed to load agents');
      }

      const data = await response.json();
      setAgents(data.agents || []);
    } catch (err) {
      console.error('Error loading agents:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleStartChat = (agentId: string) => {
    const guid = crypto.randomUUID ? crypto.randomUUID() :
      'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
        const r = Math.random() * 16 | 0;
        const v = c == 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
      });
    const timestamp = Date.now();
    const threadId = `${agentId}-${guid}-${timestamp}`;
    route(`${scope.getNavigationPrefix()}/chat/${agentId}/${threadId}`);
  };


  if (loading) {
    return (
      <MainLayout>
        <div class="flex items-center justify-center h-full">
          <span class="loading loading-spinner loading-lg"></span>
        </div>
      </MainLayout>
    );
  }

  if (error || !team) {
    return (
      <MainLayout>
        <div class="flex-1 overflow-auto">
          <div class="container mx-auto p-8 max-w-6xl">
            <div class="alert alert-error">
              <span>{error || 'Team not found'}</span>
            </div>
            <div class="mt-4">
              <a href="/teams" class="btn btn-ghost">← Back to Teams</a>
            </div>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div class="flex-1 overflow-auto">
        {/* Gradient Background Container */}
        <div class={`min-h-full ${pageGradients.combined}`}>
          {/* Decorative Elements */}
          <div class={`absolute top-20 right-20 w-96 h-96 ${decorativeBlobs.topRight.light} ${decorativeBlobs.topRight.dark} rounded-full blur-3xl pointer-events-none`}></div>
          <div class={`absolute bottom-20 left-20 w-80 h-80 ${decorativeBlobs.bottomLeft.light} ${decorativeBlobs.bottomLeft.dark} rounded-full blur-3xl pointer-events-none`}></div>

          <div class="relative container mx-auto px-4 py-12 max-w-7xl">
            {/* Header - Centered */}
            <div class="text-center mb-12">
              <h1 class={`text-5xl md:text-6xl font-extrabold mb-4 ${headingGradient.combined}`}>
                {team.name}
              </h1>
              {team.description && (
                <p class="text-xl text-base-content/70 max-w-2xl mx-auto">
                  {team.description}
                </p>
              )}

              {/* Quick Actions - Minimal */}
              <div class="flex gap-3 justify-center mt-6">
                {userRole === 'admin' && (
                  <>
                    <a href={`/teams/${teamId}/settings`} class="btn btn-ghost btn-sm">
                      Settings
                    </a>
                    <a href={`/teams/${teamId}/invite`} class="btn btn-primary btn-sm">
                      + Invite Members
                    </a>
                  </>
                )}
              </div>
            </div>

            {/* AI Agents Section */}
            <div class="mb-12">
              <h2 class="text-3xl font-bold mb-8 text-center">AI Agents</h2>

              {agents.length === 0 ? (
                <div class="bg-base-100/80 backdrop-blur-sm rounded-3xl shadow-xl p-12 text-center">
                  <div class="text-6xl mb-4">🤖</div>
                  <h3 class="text-2xl font-semibold mb-2">No agents yet</h3>
                  <p class="text-base-content/70 mb-6">
                    Create your first AI agent to get started
                  </p>
                  <button class="btn btn-primary">Create Agent</button>
                </div>
              ) : (
                <div class="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                  {agents.map((agent, index) => (
                    <button
                      key={agent.id}
                      onClick={() => handleStartChat(agent.id)}
                      class={`btn ${getAgentCardColor(index)} rounded-2xl p-6 h-auto min-h-[12rem] shadow-lg hover:shadow-2xl hover:scale-105 transition-all duration-300 text-left flex flex-col items-start`}
                    >
                      <div class="flex flex-col h-full w-full">
                        <h3 class="text-2xl font-bold mb-2">{agent.name}</h3>
                        <p class="opacity-90 text-sm mb-4">{agent.role || agent.description || 'AI Assistant'}</p>

                        <div class="mt-auto pt-4 self-end">
                          <div class="w-16 h-16 bg-base-100/20 rounded-full backdrop-blur-sm flex items-center justify-center text-3xl">
                            🤖
                          </div>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Secondary Actions */}
            <div class="grid grid-cols-1 md:grid-cols-4 gap-4 max-w-5xl mx-auto">
              <a
                href={`/teams/${teamId}/connectors`}
                class="bg-base-100/80 backdrop-blur-sm rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 text-center group"
              >
                <div class="text-4xl mb-3 group-hover:scale-110 transition-transform">🔌</div>
                <h3 class="font-semibold text-lg mb-1">Connectors</h3>
                <p class="text-sm text-base-content/70">Browse available tools</p>
              </a>

              <a
                href={`/teams/${teamId}/connections`}
                class="bg-base-100/80 backdrop-blur-sm rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 text-center group"
              >
                <div class="text-4xl mb-3 group-hover:scale-110 transition-transform">🔗</div>
                <h3 class="font-semibold text-lg mb-1">Connections</h3>
                <p class="text-sm text-base-content/70">Manage integrations</p>
              </a>

              <a
                href={`/teams/${teamId}/files`}
                class="bg-base-100/80 backdrop-blur-sm rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 text-center group"
              >
                <div class="text-4xl mb-3 group-hover:scale-110 transition-transform">📄</div>
                <h3 class="font-semibold text-lg mb-1">Team Files</h3>
                <p class="text-sm text-base-content/70">Upload knowledge files</p>
              </a>

              <a
                href={`/teams/${teamId}/skills`}
                class="bg-base-100/80 backdrop-blur-sm rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 text-center group"
              >
                <div class="text-4xl mb-3 group-hover:scale-110 transition-transform">🧠</div>
                <h3 class="font-semibold text-lg mb-1">Skills</h3>
                <p class="text-sm text-base-content/70">Procedures agents recall</p>
              </a>

              <a
                href={`/teams/${teamId}/settings`}
                class="bg-base-100/80 backdrop-blur-sm rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 text-center group"
              >
                <div class="text-4xl mb-3 group-hover:scale-110 transition-transform">👥</div>
                <h3 class="font-semibold text-lg mb-1">Team Members</h3>
                <p class="text-sm text-base-content/70">{team.memberCount} member{team.memberCount !== 1 ? 's' : ''}</p>
              </a>
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
}
